import { useEffect, useState } from "react"
import {
	isBooleanScalarType,
	isNumericScalarType,
} from "@/lib/node-description/value-types"
import { cn } from "@/lib/utils"
import { NodePill } from "../nodes/primitives/node-pill"

type ScalarValuePillProps = {
	value: unknown
	type: string
	onChange: (value: unknown) => void
	className?: string
	variant?: "canvas" | "sidebar" | "rail" | "named"
	emphasized?: boolean
	disabled?: boolean
}

const parseScalarInput = (rawValue: string, type: string): unknown => {
	if (isNumericScalarType(type)) {
		const parsed = Number.parseFloat(rawValue)
		return Number.isNaN(parsed) ? 0 : parsed
	}

	if (isBooleanScalarType(type)) {
		return rawValue.toLowerCase() === "true"
	}

	return rawValue
}

export const ScalarValuePill = ({
	value,
	type,
	onChange,
	className,
	variant = "canvas",
	emphasized = false,
	disabled = false,
}: ScalarValuePillProps) => {
	const [inputValue, setInputValue] = useState(String(value ?? ""))
	const [isFocused, setIsFocused] = useState(false)
	const inputSize = Math.max(1, String(inputValue || "0").length)

	const baseClassName =
		variant === "sidebar"
			? "h-7 min-w-10 rounded-lg border border-transparent bg-muted/60 px-2 text-center font-mono text-sm text-foreground transition-colors hover:border-border/60 hover:bg-muted focus:border-primary/60 focus:bg-background focus:outline-none focus:ring-2 focus:ring-primary/35"
			: variant === "rail"
				? cn(
						"h-full w-full border-0 bg-transparent px-0 text-center text-[20px] leading-none font-normal tabular-nums text-[var(--node-text)] focus:outline-none",
						emphasized && "font-semibold",
					)
				: variant === "named"
					? "min-w-[1ch] bg-transparent px-0 text-center text-[12px] leading-[normal] font-medium tabular-nums text-[var(--node-text)] focus:outline-none disabled:cursor-default"
					: cn(
							"h-[26px] min-w-[46px] rounded-[20px] border-[3px] px-[12px] text-center text-[12px] leading-[normal] font-medium tabular-nums text-[var(--node-text)] transition-colors",
							"border-[var(--node-pill-border)] bg-[var(--node-pill-bg)] shadow-[var(--node-pill-shadow)]",
							"hover:border-[var(--node-pill-border-hover)] hover:bg-[var(--node-pill-bg-active)]",
							"focus:border-[var(--node-pill-border-active)] focus:bg-[var(--node-pill-bg-active)] focus:outline-none focus:ring-0",
							emphasized &&
								"border-[var(--node-pill-border-active)] bg-[var(--node-pill-bg-active)]",
						)

	useEffect(() => {
		setInputValue(String(value ?? ""))
	}, [value])

	return variant === "canvas" ? (
		<NodePill
			className={cn(
				"focus-within:border-[var(--node-pill-border-active)]",
				className,
			)}
			tone="scalar"
			selected={emphasized || isFocused}
			interactive
		>
			<input
				type="text"
				inputMode={isNumericScalarType(type) ? "decimal" : "text"}
				value={inputValue}
				disabled={disabled}
				size={inputSize}
				onChange={(event) => setInputValue(event.target.value)}
				onBlur={() => {
					if (!disabled) onChange(parseScalarInput(inputValue, type))
				}}
				onFocus={(event) => {
					setIsFocused(true)
					event.target.select()
				}}
				onPointerDown={(event) => event.stopPropagation()}
				onClick={(event) => event.stopPropagation()}
				onKeyDown={(event) => {
					if (event.key === "Enter" || event.key === "Escape") {
						event.currentTarget.blur()
					}
					event.stopPropagation()
				}}
				onBlurCapture={() => setIsFocused(false)}
				className="relative min-w-0 bg-transparent px-0 text-center text-[12px] leading-[normal] font-medium tabular-nums text-[var(--node-text)] focus:outline-none"
			/>
		</NodePill>
	) : (
		<input
			type="text"
			inputMode={isNumericScalarType(type) ? "decimal" : "text"}
			value={inputValue}
			size={variant === "named" ? inputSize : undefined}
			disabled={disabled}
			onChange={(event) => setInputValue(event.target.value)}
			onBlur={() => {
				if (!disabled) onChange(parseScalarInput(inputValue, type))
			}}
			onFocus={(event) => event.target.select()}
			onPointerDown={(event) => event.stopPropagation()}
			onClick={(event) => event.stopPropagation()}
			onKeyDown={(event) => {
				if (event.key === "Enter" || event.key === "Escape") {
					event.currentTarget.blur()
				}
				event.stopPropagation()
			}}
			className={cn(baseClassName, className)}
		/>
	)
}
